import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { ScheduleModal } from "@/components/ScheduleModal";
import { Reviews } from "@/components/Reviews";
import { FeatureSlides } from "@/components/FeatureSlides";
import { PricingCompare } from "@/components/PricingCompare";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Code2, Gauge, ShieldCheck, ArrowRight, Boxes, KeyRound, Sparkles, PhoneCall,
} from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1746470427686-4c3551f3d689";
const MEET_IMG = "https://images.pexels.com/photos/4343028/pexels-photo-4343028.jpeg";

export default function Home() {
  const [modal, setModal] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleSchedule = () => {
    if (user) {
      setModal(true);
    } else {
      toast.info("Please create a free account to request a call back.");
      navigate("/register");
    }
  };

  return (
    <div className="grain min-h-screen bg-background text-white">
      <Header onSchedule={handleSchedule} />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div
          className="pointer-events-none absolute inset-0 opacity-40"
          style={{
            backgroundImage: `url(${HERO_IMG})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            maskImage: "linear-gradient(to bottom, black 20%, transparent 95%)",
            WebkitMaskImage: "linear-gradient(to bottom, black 20%, transparent 95%)",
          }}
        />
        <div className="relative mx-auto max-w-7xl px-6 pb-24 pt-24 sm:pt-32">
          <div className="fade-up max-w-3xl">
            <span className="inline-flex items-center gap-2 border border-white/10 bg-white/[0.03] px-3 py-1 font-mono text-xs uppercase tracking-[0.2em] text-primary">
              <Sparkles className="h-3.5 w-3.5" /> Custom-built. Owned by you.
            </span>
            <h1 className="mt-6 font-display text-5xl font-black uppercase leading-[1.05] tracking-tighter sm:text-6xl lg:text-7xl">
              High-performing<br />
              storefronts,<br />
              <span className="text-primary" style={{ textShadow: "0 0 40px rgba(59,130,246,0.5)" }}>
                engineered for speed.
              </span>
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-zinc-300 sm:text-lg">
              We build custom digital storefronts — no bloated website builders. You get the pure
              source code and 100% ownership of your digital asset.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Button
                data-testid="hero-schedule-btn"
                onClick={handleSchedule}
                className="rounded-full bg-primary px-7 py-6 text-base font-medium text-white hover:bg-primary/90 hover:shadow-[0_0_30px_rgba(59,130,246,0.6)] transition-[background-color,box-shadow]"
              >
                <PhoneCall className="mr-2 h-5 w-5" /> Request a Call Back
              </Button>
              <Button
                data-testid="hero-login-btn"
                variant="ghost"
                onClick={() => navigate(user ? (user.role === "admin" ? "/admin" : "/dashboard") : "/login")}
                className="rounded-full border border-white/15 px-7 py-6 text-base text-zinc-200 hover:bg-white/5 hover:text-white"
              >
                {user ? "Go to Dashboard" : "Client Login"} <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* VALUE BENTO */}
      <section className="relative mx-auto max-w-7xl px-6 py-20">
        <div className="mb-10">
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-zinc-500">// What you get</p>
          <h2 className="mt-2 font-display text-3xl font-bold tracking-tight sm:text-4xl">
            Built right. Owned outright.
          </h2>
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <BentoCard className="md:col-span-2 md:row-span-1" testid="value-ownership"
            icon={Code2} title="You own the source code"
            body="We don't lock you into bloated builders. When you work with us, you receive the pure custom source code — giving you 100% ownership of your digital asset, forever." />
          <BentoCard testid="value-speed" icon={Gauge} title="Engineered for speed"
            body="Lean, hand-crafted code with no builder overhead — fast to load, fast to convert." />
          <BentoCard testid="value-timeline" icon={Boxes} title="Straightforward projects"
            body="Every build is scoped to your needs. Pricing and timeframe are discussed together on a quick call — no surprises." />
          <BentoCard className="md:col-span-2" testid="value-account"
            icon={KeyRound} title="Your own client dashboard, free"
            body="Every client gets a free account to track build progress in real time and message us directly — start to finish, on us." />
        </div>
      </section>

      <FeatureSlides />
      <PricingCompare onSchedule={handleSchedule} />

      {/* MEETING CTA */}
      <section className="relative mx-auto max-w-7xl px-6 py-16">
        <div className="beam-border relative grid grid-cols-1 items-center gap-8 overflow-hidden border border-white/10 bg-[#0A0A0B] p-8 md:grid-cols-2 md:p-12">
          <div>
            <ShieldCheck className="h-9 w-9 text-primary" />
            <h2 className="mt-5 font-display text-3xl font-bold tracking-tight sm:text-4xl">
              Let's build something you own.
            </h2>
            <p className="mt-3 max-w-md text-zinc-300">
              Book a quick call. We'll walk through your project, scope, pricing and timeline —
              then get you set up with a dashboard to track everything.
            </p>
            <Button
              data-testid="cta-schedule-btn"
              onClick={handleSchedule}
              className="mt-6 rounded-full bg-primary px-7 py-6 text-base text-white hover:bg-primary/90 hover:shadow-[0_0_30px_rgba(59,130,246,0.6)] transition-[background-color,box-shadow]"
            >
              <PhoneCall className="mr-2 h-5 w-5" /> Request a Call Back
            </Button>
          </div>
          <div className="relative h-56 overflow-hidden border border-white/10 md:h-72">
            <img src={MEET_IMG} alt="Call back" className="h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
          </div>
        </div>
      </section>

      <Reviews />

      <footer className="border-t border-white/10 py-10">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-3 px-6 sm:flex-row">
          <p className="font-display font-black tracking-tighter text-lg">
            SITES<span className="text-primary"> UX</span>
          </p>
          <div className="flex items-center gap-5">
            <button
              data-testid="footer-support-link"
              onClick={() => navigate("/support")}
              className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-400 transition-colors hover:text-primary"
            >
              Support
            </button>
            <p className="font-mono text-xs text-zinc-500">© 2026 Sites UX — you own the code.</p>
          </div>
        </div>
      </footer>

      <ScheduleModal open={modal} onOpenChange={setModal} />
    </div>
  );
}

function BentoCard({ icon: Icon, title, body, className = "", testid }) {
  return (
    <div
      data-testid={testid}
      className={`group border border-white/10 bg-[#0A0A0B] p-6 transition-[transform,border-color] duration-300 hover:-translate-y-1 hover:border-white/25 ${className}`}
    >
      <div className="flex h-10 w-10 items-center justify-center border border-white/10 bg-white/[0.03] text-primary transition-colors group-hover:border-primary/50">
        <Icon className="h-5 w-5" />
      </div>
      <h3 className="mt-5 font-display text-xl font-semibold">{title}</h3>
      <p className="mt-2 text-sm leading-relaxed text-zinc-400">{body}</p>
    </div>
  );
}
